import { useCallback, useEffect, useRef, useState } from "react";
import { bootstrapEegDetachedWindowDocument } from "../components/eegDetachedWindowBootstrap";
import {
  computeCompactAnnotationToolbarPopoutHeightPx,
  openEegDetachedWindow,
} from "../components/eegDetachedWindowUtils";

export const FLOATING_ANNOTATION_TOOLBAR_POPOUT_WINDOW_NAME =
  "eeg-floating-annotation-toolbar";

const FLOATING_ANNOTATION_TOOLBAR_GEOMETRY_KEY =
  "eeg-popout-annotation-set-geometry";

const DEFAULT_POPOUT_WIDTH = 177;
const DEFAULT_POPOUT_HEIGHT = computeCompactAnnotationToolbarPopoutHeightPx();
const FLOATING_TOOLBAR_PANEL_TITLE = "Annotation Set";
const FLOATING_TOOLBAR_PANEL_KEY = "floating-annotation-toolbar";

interface DocumentPictureInPictureLike {
  requestWindow: (options?: {
    width?: number;
    height?: number;
    disallowReturnToOpener?: boolean;
    preferInitialWindowPlacement?: boolean;
  }) => Promise<Window>;
}

function getDocumentPictureInPicture(): DocumentPictureInPictureLike | null {
  if (typeof window === "undefined") return null;
  const candidate = (window as Window & {
    documentPictureInPicture?: DocumentPictureInPictureLike;
  }).documentPictureInPicture;
  return candidate && typeof candidate.requestWindow === "function"
    ? candidate
    : null;
}

function readStoredGeometry(): { width: number; height: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(FLOATING_ANNOTATION_TOOLBAR_GEOMETRY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { width?: number; height?: number };
    if (
      !parsed ||
      typeof parsed.width !== "number" ||
      typeof parsed.height !== "number"
    ) {
      return null;
    }
    return {
      width: Math.max(160, Math.round(parsed.width)),
      height: Math.max(280, Math.round(parsed.height)),
    };
  } catch {
    return null;
  }
}

function clampPopoutGeometry(width: number, height: number): {
  width: number;
  height: number;
} {
  if (typeof window === "undefined") {
    return { width, height };
  }
  const viewportWidth = Math.max(160, window.innerWidth || width);
  const viewportHeight = Math.max(240, window.innerHeight || height);
  const maxWidth = Math.max(144, viewportWidth - 16);
  const maxHeight = Math.max(220, viewportHeight - 16);
  return {
    width: Math.min(Math.max(160, Math.round(width)), maxWidth),
    height: Math.min(Math.max(240, Math.round(height)), maxHeight),
  };
}

export function useFloatingToolbarPopout() {
  const windowRef = useRef<Window | null>(null);
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const [isPoppedOut, setIsPoppedOut] = useState(false);

  const popIn = useCallback(() => {
    const win = windowRef.current;
    if (win && !win.closed) {
      try {
        win.close();
      } catch {
        // ignore close failures
      }
    }
    windowRef.current = null;
    setPopoutWindow(null);
    setIsPoppedOut(false);
  }, []);

  const popOut = useCallback(async (): Promise<boolean> => {
    const existing = windowRef.current;
    if (existing && !existing.closed) {
      existing.focus();
      setPopoutWindow(existing);
      setIsPoppedOut(true);
      return true;
    }

    const stored = readStoredGeometry();
    const { width, height } = clampPopoutGeometry(
      stored?.width ?? DEFAULT_POPOUT_WIDTH,
      stored?.height ?? DEFAULT_POPOUT_HEIGHT,
    );
    const pictureInPicture = getDocumentPictureInPicture();
    let win: Window | null = null;

    if (pictureInPicture) {
      try {
        win = await pictureInPicture.requestWindow({
          width,
          height,
          disallowReturnToOpener: true,
          preferInitialWindowPlacement: true,
        });
        bootstrapEegDetachedWindowDocument(win, {
          title: FLOATING_TOOLBAR_PANEL_TITLE,
          panelKey: FLOATING_TOOLBAR_PANEL_KEY,
          compactToolbar: true,
        });
      } catch {
        win = null;
      }
    }

    if (!win) {
      win = openEegDetachedWindow({
        title: FLOATING_TOOLBAR_PANEL_TITLE,
        panelKey: FLOATING_TOOLBAR_PANEL_KEY,
        windowName: FLOATING_ANNOTATION_TOOLBAR_POPOUT_WINDOW_NAME,
        width,
        height,
        compactToolbar: true,
      });
    }

    if (!win) {
      return false;
    }

    try {
      win.focus();
    } catch {
      // Browsers may ignore focus for background popups.
    }

    windowRef.current = win;
    setPopoutWindow(win);
    setIsPoppedOut(true);
    return true;
  }, []);

  const focusIfOpen = useCallback((): boolean => {
    const win = windowRef.current;
    if (win && !win.closed) {
      win.focus();
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!isPoppedOut) return;

    const poll = window.setInterval(() => {
      const win = windowRef.current;
      if (!win || win.closed) {
        windowRef.current = null;
        setPopoutWindow(null);
        setIsPoppedOut(false);
      }
    }, 500);

    return () => window.clearInterval(poll);
  }, [isPoppedOut]);

  useEffect(
    () => () => {
      const win = windowRef.current;
      if (win && !win.closed) {
        try {
          win.close();
        } catch {
          // ignore close failures on unmount
        }
      }
    },
    [],
  );

  return {
    isPoppedOut,
    popoutWindow,
    popOut,
    popIn,
    focusIfOpen,
  };
}
